import {
	Box,
	Button,
	Flex,
	Popover,
	PopoverBody,
	PopoverContent,
	Image,
	PopoverTrigger,
	Text,
	Divider,
} from 'src/components/ui';
import { HamburgerIcon } from '@chakra-ui/icons';
import { useContext, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import UserLogo from 'src/components/common/UserLogo';
import UserContext from 'src/contexts/user/UserContext';
import { CampaignContext } from 'src/contexts';
import { useOnboarding } from 'src/contexts/onboarding';
import authService from 'src/services/auth';
import { MdOutlineLogout } from 'react-icons/md';
import Logo from 'src/assets/svgs/logo.svg';
import { TOP_BAR_HEIGHT } from 'src/components/campaigns/utils/constants';
import { useLeftSidebarContext } from 'src/contexts/app/LeftSidebarContext';
import { useRightSidebarContext } from 'src/contexts/app/RightSidebarContext';
import { humanizeTime } from 'src/lib/utils';
import UserAvatar from 'src/components/common/UserAvatar';

const TopBar = () => {
	const [isLoggingOut, setIsLoggingOut] = useState(false);
	const [lastEditTimeMessage, setLastEditTimeMessage] = useState('');
	const [isMobileDevice, setIsMobileDevice] = useState(
		typeof window !== 'undefined' && window.innerWidth < 768
	);
	const { user, setUser } = useContext(UserContext);
	const navigate = useNavigate();
	const location = useLocation();
	const { campaign, isEditorMode } = useContext(CampaignContext);
	const { showCampaignFormTour } = useOnboarding();

	const handleLogout = async () => {
		setIsLoggingOut(true);
		await authService.logout();
		setUser(null);
		setIsLoggingOut(false);
		navigate('/login');
	};

	const isCampaignPath = location.pathname.includes('/projects/campaigns/');
	const { toggleSidebar } = useLeftSidebarContext();
	const { isSidebarOpen: isRightSidebarOpen, handleCloseSidebar: closeRightSidebar } = useRightSidebarContext();

	const handleHamburgerClick = (e: React.MouseEvent) => {
		console.log('🍔 [TopBar] Hamburger clicked!', {
			isMobileDevice,
			isCampaignPath,
			isRightSidebarOpen,
			event: e,
			target: e.target,
			currentTarget: e.currentTarget
		});

		// Prevent event from being blocked
		e.stopPropagation();

		// On mobile, close the right sidebar (copilot) first
		if (isRightSidebarOpen) {
			console.log('🍔 [TopBar] Closing right sidebar first');
			closeRightSidebar();
		}
		// Then toggle the left sidebar
		console.log('🍔 [TopBar] Toggling left sidebar');
		toggleSidebar();
	};

	// Log component mount and key state
	useEffect(() => {
		console.log('🍔 [TopBar] Component mounted/updated', {
			isCampaignPath,
			isMobileDevice,
			shouldShowHamburger: isMobileDevice || isCampaignPath,
			pathname: location.pathname
		});
	}, [isCampaignPath, isMobileDevice, location.pathname]);

	// Track window resize for mobile detection
	useEffect(() => {
		const handleResize = () => {
			const wasMobile = isMobileDevice;
			const isNowMobile = window.innerWidth < 768;
			setIsMobileDevice(isNowMobile);
			if (wasMobile !== isNowMobile) {
				console.log('🍔 [TopBar] Device type changed:', {
					wasMobile,
					isNowMobile,
					width: window.innerWidth
				});
			}
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [isMobileDevice]);

	// Update last edit time message
	useEffect(() => {
		if (!campaign?.updatedAt) {
			setLastEditTimeMessage('');
			return;
		}

		const updateMessage = () => {
			const time = `Last edit ${humanizeTime(campaign.updatedAt)}`;
			setLastEditTimeMessage(time);
		};

		updateMessage();
		const intervalId = setInterval(updateMessage, 60000); // Update every minute

		return () => clearInterval(intervalId);
	}, [campaign?.updatedAt]);

	return (
		<Box
			w="full"
			position="fixed"
			top={0}
			left={0}
			right={0}
			zIndex={10000}
			bg="white"
			boxShadow="0 0px 15px -3px rgba(0, 0, 0, 0.1)"
			pointerEvents="auto"
			transition="background-color 0.2s ease-in-out"
		>
			<Box h={TOP_BAR_HEIGHT}>
			<Flex
				ml={{ base: 0, md: '20px' }}
				h="full"
				justifyContent="space-between"
				alignItems="center"
				px={{ base: 2, md: 0 }}
			>
				<Flex h="full" alignItems="center" gap={2} position="relative" zIndex={10001}>
					{/* Hamburger menu - visible on mobile or on desktop when in campaign pages */}
					<Box
						as="button"
						cursor="pointer"
						aria-label="Open menu"
						onClick={handleHamburgerClick}
						onMouseEnter={() => console.log('🍔 [TopBar] Mouse entered hamburger area')}
						onMouseDown={() => console.log('🍔 [TopBar] Mouse down on hamburger')}
						onTouchStart={() => console.log('🍔 [TopBar] Touch start on hamburger')}
						bg="transparent"
						height="100%"
						display={{ base: 'flex', md: isCampaignPath ? 'flex' : 'none' }}
						alignItems="center"
						justifyContent="center"
						zIndex={10001}
						px={3}
						pointerEvents="auto"
						position="relative"
						_hover={{ bg: 'transparent', opacity: 0.7 }}
						_active={{ opacity: 0.5 }}
						border="none"
						outline="none"
					>
						<HamburgerIcon
							fontSize={{ base: '20px', md: '24px' }}
							height={{ base: '24px', md: '30px' }}
							width={{ base: '24px', md: '30px' }}
							color="gray.700"
							pointerEvents="none"
						/>
					</Box>

					{/* Show logo on non-campaign pages, mobile, or when campaign not loaded. Show campaign info on desktop campaign pages with loaded campaign */}
					{!isCampaignPath || isMobileDevice || !campaign ? (
						<Link to="/home">
							<Image src={Logo} height={{ base: '24px', md: '30px' }} />
						</Link>
					) : (
						<Flex
							alignItems="center"
							gap={3}
							display={{ base: 'none', md: 'flex' }}
							flexWrap="wrap"
							maxW="calc(100vw - 300px)"
						>
							<Text
								fontSize="14px"
								fontWeight="semibold"
								isTruncated
								maxW="300px"
							>
								{campaign.name || 'New Campaign'}
							</Text>
							{campaign.group && (
								<>
									<Divider orientation="vertical" h="20px" />
									<Text fontSize="14px" color="gray.600">
										Group: {campaign.group}
									</Text>
								</>
							)}
							{lastEditTimeMessage && (
								<>
									<Divider orientation="vertical" h="20px" />
									<Flex alignItems="center" gap={2}>
										<Text fontSize="12px" color="gray.500">
											{lastEditTimeMessage}
										</Text>
										{campaign.lastUpdatedBy?.name && (
											<UserAvatar name={campaign.lastUpdatedBy.name} />
										)}
									</Flex>
								</>
							)}
						</Flex>
					)}
				</Flex>

				{user && (
					<Flex gap={3} mr={{ base: 0, md: '30px' }}>
						<Popover placement="bottom-end">
							<PopoverTrigger>
								<Button bg="transparent" _hover={{ bg: 'transparent' }} p={0}>
									<Flex alignItems="center" gap={2}>
										<Text
											color="#414141"
											fontSize={{ base: '12px', md: '14px' }}
											fontWeight={500}
											letterSpacing="0.2px"
											fontFamily="Noto Sans"
											display={{ base: 'none', sm: 'block' }}
										>
											Hello {user.name}{' '}
										</Text>
										<UserLogo size={{ h: '33px', w: '33px' }} />
									</Flex>
								</Button>
							</PopoverTrigger>
							<PopoverContent
								_focusVisible={{
									outline: 'none',
								}}
								boxShadow={'0px 1px 4px 0px rgba(0, 0, 0, 0.25)'}
								mt={2}
								width="fit-content"
							>
								<PopoverBody bg="#FFF" borderRadius="6px" p="16px" minW="250px">
									<Flex
										direction="column"
										gap={3}
										p={3}
										borderRadius="8px"
										alignItems="center"
										bg="white"
									>
										<UserLogo size={{ h: '33px', w: '33px' }} />
										<Flex direction="column" alignItems="center">
											<Text color="#26272B" fontWeight={600} fontSize="12px">
												{user.name}
											</Text>
											<Text color="#757588" fontWeight={400} fontSize="12px">
												{user.email || user.phone || ''}
											</Text>
										</Flex>
									</Flex>
									<Flex mt={3}>
										<Button
											leftIcon={<MdOutlineLogout size={20} />}
											bg={'orange.primary'}
											color="white"
											onClick={handleLogout}
											isLoading={isLoggingOut}
											loadingText="Logging out..."
											_hover={{ bg: 'orange.primary' }}
											w="full"
										>
											<Text
												color="white"
												fontWeight={600}
												fontSize="12px"
												ml="-5px"
											>
												Logout
											</Text>
										</Button>
									</Flex>
								</PopoverBody>
							</PopoverContent>
						</Popover>
					</Flex>
				)}
			</Flex>
			</Box>
		</Box>
	);
};

export default TopBar;
